import React, { useState } from 'react';
import { SearchIcon } from 'evergreen-ui';
import AppButton from '../../../components/AppButton/AppButton';
import { SearchInputContainer, StyledTextField } from './Style';
import FilterModal from './FilterModal/FilterModal';
import { useNavigate } from 'react-router-dom';
import { buildSearchUrl, countActiveFilters } from '../utils';

interface FilterDateRange {
    from?: string;
    to?: string;
}

export interface Filters {
    invitedAt?: FilterDateRange;
    reportGeneratedAt?: FilterDateRange;
}

interface SearchInputProps {
    companyId?: string;
    groupId?: string;
    initialQuery?: string;
    initialFilters?: Filters;
    initialSort?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
    companyId,
    groupId,
    initialQuery = '',
    initialFilters = {},
    initialSort = '_score:desc',
}) => {
    const navigate = useNavigate();

    const [openFilters, setOpenFilters] = useState(false);
    const [query, setQuery] = useState(() => initialQuery ?? '');
    const [filters, setFilters] = useState<Filters>(() => initialFilters ?? {});
    const [sort, setSort] = useState(() => initialSort ?? '_score:desc');

    const activeFilterCount = countActiveFilters(filters, sort);

    const handleSearch = () => {
        const url = buildSearchUrl({
            query,
            filters,
            sort,
            companyId,
            groupId,
        });
        navigate(url);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleApplyFilters = (newFilters: Filters, newSort: string) => {
        setFilters(newFilters);
        setSort(newSort);
        setOpenFilters(false);
    };

    const handleClearFilters = () => {
        const clearedFilters: Filters = {};
        const defaultSort = '_score:desc';
        setFilters(clearedFilters);
        setSort(defaultSort);
    };

    return (
        <SearchInputContainer>
            <AppButton
                color="gray"
                fontSize="14px"
                onClick={() => setOpenFilters(true)}>
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </AppButton>
            <StyledTextField
                variant="outlined"
                placeholder="Search candidates"
                fullWidth
                className="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <AppButton icon={SearchIcon} onClick={handleSearch} fontSize="14px">
                Search
            </AppButton>

            <FilterModal
                open={openFilters}
                onClose={() => setOpenFilters(false)}
                filters={filters}
                sort={sort}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
            />
        </SearchInputContainer>
    );
};

export default SearchInput;
